import { useCallback } from 'react';
import { useSidebar } from '@/components/ui/sidebar';
import { useIsMobile } from '@/hooks/layout/use-mobile';

/**
 * Custom hook for research navigation
 * Handles sidebar expansion and scrolling to generate button on desktop
 * Opens mobile accordion on mobile devices
 */
export const useResearchNavigation = () => {
  const { open, setOpen } = useSidebar();
  const { isMobile } = useIsMobile();
  
  /**
   * Navigate to the generate research section
   * Desktop: Ensures the sidebar is expanded (main sidebar contracted) and scrolls to generate button
   * Mobile: Opens the mobile accordion and scrolls to generate button
   */
  const navigateToGenerateResearch = useCallback(() => {
    if (isMobile) {
      // Mobile: Open the accordion by setting its localStorage state to expanded
      const persistKey = 'research-assistant-sidebar';
      if (typeof window !== 'undefined') {
        localStorage.setItem(`sidebar-accordion-${persistKey}`, JSON.stringify(true));
        
        // Trigger a storage event to update any listening components
        window.dispatchEvent(new StorageEvent('storage', {
          key: `sidebar-accordion-${persistKey}`,
          newValue: JSON.stringify(true)
        }));
      }
      
      // Wait for accordion animation to complete, then scroll to generate button
      setTimeout(() => {
        const generateButton = document.querySelector('[data-test-id="generate-research-button"]');
        if (generateButton) {
          generateButton.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 400); // Slightly longer delay for accordion animation
    } else {
      // Desktop: Original logic for sidebar manipulation
      if (open) {
        // Set sidebar state
        setOpen(false);
        
        // Wait for transition to complete (300ms as per SidebarLayout transition)
        setTimeout(() => {
          // Get the generate button element by its data attribute
          const generateButton = document.querySelector('[data-test-id="generate-research-button"]');
          
          // If the element exists, scroll to it
          if (generateButton) {
            generateButton.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 350); // Add a little extra time to ensure transition completes
      } else {
        // If sidebar is already in the right state, just scroll
        const generateButton = document.querySelector('[data-test-id="generate-research-button"]');
        if (generateButton) {
          generateButton.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }
  }, [open, setOpen, isMobile]);
  
  return {
    navigateToGenerateResearch
  };
};
